/**
 * Utility per il rendering del Gantt su scale multiple:
 * giornaliera / settimanale / mensile / trimestrale / annuale.
 *
 * Tutto il modulo lavora in giorni-UTC-ISO (YYYY-MM-DD) per evitare
 * bug di fuso orario, e converte all'ultimo momento in Date native.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export const VIEW_MODES = ["giornaliera", "settimanale", "mensile", "trimestrale", "annuale"];

const RANGE_DAYS = {
  giornaliera: 21,   // ~3 settimane centrate su today
  settimanale: 8 * 7, // 8 settimane
  mensile: 6 * 30,   // 6 mesi
  trimestrale: 4 * 90, // 4 trimestri = 1 anno
  annuale: 5 * 365,  // 5 anni
};

export function isoDate(d) {
  if (typeof d === "string") return d.slice(0, 10);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

export function parseISO(s) {
  if (!s) return null;
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  if (!y) return null;
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
}

export function addDays(d, n) {
  const nd = new Date(d.getTime());
  nd.setUTCDate(nd.getUTCDate() + n);
  return nd;
}

export function startOfWeek(d) {
  const nd = new Date(d.getTime());
  const wd = (nd.getUTCDay() + 6) % 7; // Monday = 0
  nd.setUTCDate(nd.getUTCDate() - wd);
  nd.setUTCHours(0, 0, 0, 0);
  return nd;
}

export function startOfMonth(d) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

export function startOfQuarter(d) {
  const q = Math.floor(d.getUTCMonth() / 3);
  return new Date(Date.UTC(d.getUTCFullYear(), q * 3, 1));
}

export function startOfYear(d) {
  return new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
}

/**
 * Ritorna { rangeStart, rangeEnd, totalDays, ticks[] } per una data centro
 * e uno viewMode. `ticks` contiene { start: Date, label: string, offsetPct, widthPct }.
 */
export function computeScale(viewMode, center = new Date()) {
  const days = RANGE_DAYS[viewMode] || RANGE_DAYS.mensile;
  let start;
  let end;

  if (viewMode === "giornaliera") {
    start = addDays(center, -Math.floor(days / 2));
    end = addDays(start, days);
  } else if (viewMode === "settimanale") {
    start = startOfWeek(addDays(center, -Math.floor(days / 2)));
    end = addDays(start, days);
  } else if (viewMode === "mensile") {
    start = startOfMonth(addDays(center, -90));
    end = addDays(start, days);
  } else if (viewMode === "trimestrale") {
    start = startOfQuarter(addDays(center, -180));
    end = addDays(start, days);
  } else {
    start = startOfYear(addDays(center, -365 * 2));
    end = addDays(start, days);
  }

  const totalDays = Math.max(1, Math.round((end - start) / DAY_MS));
  const ticks = buildTicks(viewMode, start, end, totalDays);
  return { rangeStart: start, rangeEnd: end, totalDays, ticks };
}

function buildTicks(viewMode, start, end, totalDays) {
  const out = [];
  const push = (tickStart, tickEnd, label, extra = {}) => {
    const tStart = tickStart < start ? start : tickStart;
    const tEnd = tickEnd > end ? end : tickEnd;
    if (tEnd <= tStart) return;
    const offset = ((tStart - start) / DAY_MS / totalDays) * 100;
    const width = ((tEnd - tStart) / DAY_MS / totalDays) * 100;
    out.push({
      start: tStart,
      end: tEnd,
      label,
      offsetPct: offset,
      widthPct: width,
      ...extra,
    });
  };
  const MESI = ["gen","feb","mar","apr","mag","giu","lug","ago","set","ott","nov","dic"];

  if (viewMode === "giornaliera") {
    const GIORNI = ["DOM", "LUN", "MAR", "MER", "GIO", "VEN", "SAB"];

    let d = new Date(start.getTime());
    while (d < end) {
      const next = addDays(d, 1);
      const weekday = d.getUTCDay();
      const dateLabel = `${String(d.getUTCDate()).padStart(2,"0")}/${String(d.getUTCMonth()+1).padStart(2,"0")}`;

      push(
        d,
        next,
        `${GIORNI[weekday]} ${dateLabel}`,
        {
          weekdayLabel: GIORNI[weekday],
          dateLabel,
          isWeekend: weekday === 0 || weekday === 6,
          isSunday: weekday === 0,
          isSaturday: weekday === 6,
        }
      );

      d = next;
    }
  } else if (viewMode === "settimanale") {
    let d = startOfWeek(start);
    let n = 1;
    while (d < end) {
      const next = addDays(d, 7);
      push(d, next, `W${n} · ${String(d.getUTCDate()).padStart(2,"0")}/${String(d.getUTCMonth()+1).padStart(2,"0")}`);
      d = next;
      n += 1;
    }
  } else if (viewMode === "mensile") {
    let d = startOfMonth(start);
    while (d < end) {
      const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
      push(d, next, `${MESI[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`);
      d = next;
    }
  } else if (viewMode === "trimestrale") {
    let d = startOfQuarter(start);
    while (d < end) {
      const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 3, 1));
      const q = Math.floor(d.getUTCMonth() / 3) + 1;
      push(d, next, `Q${q} ${d.getUTCFullYear()}`);
      d = next;
    }
  } else {
    let d = startOfYear(start);
    while (d < end) {
      const next = new Date(Date.UTC(d.getUTCFullYear() + 1, 0, 1));
      push(d, next, `${d.getUTCFullYear()}`);
      d = next;
    }
  }
  return out;
}

/** Posizione (offset%, width%) di una attività dentro il range dello scale. */
export function activityBox(activity, scale) {
  const s = parseISO(activity.data_inizio);
  const e = parseISO(activity.data_fine);
  if (!s || !e) return null;
  const { rangeStart, totalDays } = scale;
  const offDays = (s - rangeStart) / DAY_MS;
  const dur = Math.max(0.5, (e - s) / DAY_MS + 1);
  const offset = (offDays / totalDays) * 100;
  const width = (dur / totalDays) * 100;
  // Filtra fuori dal range visibile (evita rendering di barre negative)
  if (offset + width < 0 || offset > 100) return null;
  return {
    offsetPct: Math.max(-5, offset),
    widthPct: Math.min(width, 105),
  };
}

/**
 * Trasforma uno spostamento in pixel della barra Gantt in giorni.
 * Assume che 100% == totalDays * DAY_MS su una larghezza `containerPx`.
 */
export function pxToDays(deltaPx, containerPx, totalDays) {
  if (containerPx <= 0) return 0;
  const dayPx = containerPx / totalDays;
  return Math.round(deltaPx / dayPx);
}

/** Snap dei giorni alla granularità della vista. */
export function snapDaysToView(days, viewMode) {
  if (viewMode === "settimanale") return Math.round(days / 7) * 7;
  if (viewMode === "mensile") return Math.round(days / 30) * 30;
  if (viewMode === "trimestrale") return Math.round(days / 90) * 90;
  if (viewMode === "annuale") return Math.round(days / 365) * 365;
  return days; // giornaliera
}

export function shiftDate(iso, days) {
  const d = parseISO(iso);
  if (!d) return iso;
  const nd = addDays(d, days);
  return isoDate(nd);
}
